/**
 * i18n for the admin — French first, Arabic available.
 *
 * The mobile app is Arabic-first (docs/04 §7.1). Admin is the reverse: the
 * staff who use it work in French, so `fr` is the default and `ar` is the
 * switch. The catalogues themselves are shared with the mobile app through
 * @horizon/i18n so a key exists in both files or CI fails (US-X02 AC2).
 *
 * @horizon/i18n settled on i18next instances rather than flat message records,
 * so this adapter holds one initialised instance per locale and delegates to
 * `t`. Keys are dotted paths into the shared catalogue tree.
 */
import { createI18n } from '@horizon/i18n'
import { useTranslate } from '@refinedev/core'
import type { I18nProvider } from '@refinedev/core'
import { useSyncExternalStore } from 'react'
import type { Locale } from '@/types'
import { getAdminLocale, setAdminLocale, subscribeAdminLocale } from './admin-locale-store'

export { DEFAULT_ADMIN_LOCALE } from './admin-locale-store'

type I18nInstance = ReturnType<typeof createI18n>

const INSTANCES: Record<Locale, I18nInstance> = {
  ar: createI18n('ar'),
  fr: createI18n('fr'),
}

export function getDirection(locale: Locale = getAdminLocale()): 'rtl' | 'ltr' {
  return locale === 'ar' ? 'rtl' : 'ltr'
}

/** A React subscription to the provider's mutable locale state. */
export const useAdminLocale = (): Locale =>
  useSyncExternalStore(subscribeAdminLocale, getAdminLocale, getAdminLocale)

/** Translation hook that also re-renders the caller after a locale switch. */
export const useAdminTranslate = () => {
  useAdminLocale()
  return useTranslate()
}

/** `{{count}}`-style interpolation; enough for the admin's needs. */
function interpolate(template: string, params?: Record<string, unknown>): string {
  if (!params) return template
  return template.replace(/\{\{(\w+)\}\}/g, (match, key: string) =>
    key in params ? String(params[key]) : match,
  )
}

export const i18nProvider: I18nProvider = {
  /**
   * Refine calls this with the fallback string in the params slot
   * (`translate(key, defaultMessage)`), so both shapes are accepted.
   */
  translate: (
    key: string,
    paramsOrDefault?: Record<string, unknown> | string,
    defaultMessage?: string,
  ) => {
    const params = typeof paramsOrDefault === 'string' ? undefined : paramsOrDefault
    const fallback = typeof paramsOrDefault === 'string' ? paramsOrDefault : defaultMessage
    const instance = INSTANCES[getAdminLocale()]
    if (instance.exists(key)) return instance.t(key, params ?? {})
    return interpolate(fallback ?? key, params)
  },
  getLocale: getAdminLocale,
  changeLocale: async (locale: string) => {
    const next = setAdminLocale(locale)
    document.documentElement.lang = next
    document.documentElement.dir = getDirection(next)
    return
  },
}

/**
 * Translation outside React — for `lib/` modules that format strings without a
 * component to hold a hook.
 */
export const t = (key: string, params?: Record<string, unknown>): string =>
  i18nProvider.translate(key, params) as string

/** Applied once at boot, before the first render. */
export function applyInitialDirection(): void {
  const locale = getAdminLocale()
  document.documentElement.lang = locale
  document.documentElement.dir = getDirection(locale)
}
